/**
 * ValidationPanel — the pre-submission pricing gate. Per-line validated /
 * needs-attention with landed cost, historical band and margin. Submission is
 * blocked until the overall gate passes. Also generates the Flexport freight
 * quote email used to compute landed cost.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, Send, Truck, Award } from 'lucide-react';
import { forgeAPI } from '../../../services/api';
import { useForge } from '../ForgeContext';
import { handoffToManifest } from '../handoffToManifest';

const money = (n) => (n == null ? '—' : `CAD ${Number(n).toLocaleString()}`);

const ValidationPanel = () => {
  const { tenderId, summary, refresh } = useForge();
  const navigate = useNavigate();
  const v = summary?.validation;
  const [freight, setFreight] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const pass = v?.overall_pass;

  const handoff = async () => {
    setBusy(true);
    try {
      // Re-tag shared comms threads forge -> manifest (best-effort) and flip
      // the spec sheet to submitted.
      try { await forgeAPI.markAwarded(tenderId); } catch (e) { /* non-blocking */ }
      const orderId = handoffToManifest(summary);
      navigate(`/manifest/${encodeURIComponent(orderId)}`);
    } finally {
      setBusy(false);
    }
  };

  const genFreight = async () => {
    setBusy(true);
    const res = await forgeAPI.freightEmail(tenderId, {});
    setFreight(res);
    setBusy(false);
  };

  return (
    <div className="forge-validate">
      <div className={`forge-gate-banner ${pass ? 'forge-gate-pass' : 'forge-gate-fail'}`}>
        {pass ? <ShieldCheck size={20} /> : <ShieldAlert size={20} />}
        <div>
          <b>{pass ? 'Pricing validated — ready to submit' : 'Pricing validation incomplete'}</b>
          <div className="forge-muted">{v?.summary}</div>
        </div>
      </div>

      <table className="forge-cmp-table">
        <thead>
          <tr>
            <th>Line</th><th>Product</th><th>Status</th>
            <th>Landed (CAD)</th><th>Historical band</th><th>Margin</th><th>Issues</th>
          </tr>
        </thead>
        <tbody>
          {(v?.line_results || []).map((r) => (
            <tr key={r.line_item}>
              <td>{r.line_item}</td>
              <td>{r.product_name}</td>
              <td>
                <span className={`forge-status forge-status-${r.status}`}>
                  {r.status === 'validated' ? 'Validated' : 'Needs attention'}
                </span>
              </td>
              <td>{money(r.landed_cost_cad)}</td>
              <td>{money(r.historical_price_cad)}</td>
              <td>{r.margin_pct == null ? '—' : `${r.margin_pct}%`}</td>
              <td className="forge-issues">{r.issues?.length ? r.issues.join('; ') : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="forge-validate-foot">
        <div className="forge-freight">
          <button className="forge-btn-ghost" onClick={genFreight} disabled={busy}>
            <Truck size={14} /> Generate Flexport freight email
          </button>
          {freight && (
            <pre className="forge-freight-email">{freight.body}</pre>
          )}
        </div>

        <div className="forge-submit-actions">
          <button
            className="forge-btn-primary forge-submit"
            disabled={!pass || submitted}
            onClick={() => setSubmitted(true)}
            title={pass ? 'Submit bid' : 'Validation must pass before submission'}
          >
            <Send size={15} /> {submitted ? 'Bid submitted ✓' : 'Submit bid'}
          </button>
          {submitted && (
            <button
              className="forge-btn-primary forge-award"
              disabled={busy}
              onClick={handoff}
              title="Mark as awarded and hand off to Manifest"
            >
              <Award size={15} /> {busy ? 'Handing off…' : 'Mark as awarded → Manifest'}
            </button>
          )}
        </div>
      </div>
      {!pass && <p className="forge-muted forge-submit-note">Submission is gated until every line item is validated.</p>}
      {submitted && (
        <p className="forge-muted forge-submit-note">
          On award, FORGE hands the finalized spec, awarded suppliers and live
          supplier threads to Manifest, which picks up at Contract &amp; Pay.
        </p>
      )}
    </div>
  );
};

export default ValidationPanel;
